import { Node, mergeAttributes, nodePasteRule } from "@tiptap/core";

import {
  type ResizableMediaOptions,
  createResizableNodeView,
} from "./resizable";

export const iframeRegex = /<iframe[^<]+<\/iframe>/gi;

export const extractHTMLTag = (match: string, attr: string) => {
  const reg = new RegExp(`${attr}="([^"]+)"`);
  const result = reg.exec(match);
  return result ? result[1] : null;
};

export type IframeAttrs = {
  src: string;
  title?: string;
  allow?: string;
  width?: number | string | null;
  height?: number | string | null;
};

export type IframeOptions = { resize?: ResizableMediaOptions };

declare module "@tiptap/core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Commands<ReturnType> {
    iframe: { addIframe: (attrs: IframeAttrs) => ReturnType };
  }
}

const DEFAULT_MIN_WIDTH = 200;
const DEFAULT_MIN_HEIGHT = 150;
const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 360;

export const Iframe = Node.create<IframeOptions>({
  name: "iframe",

  group: "block",

  atom: true,

  selectable: true,

  draggable: true,

  addOptions() {
    return {
      resize: {
        enabled: true,
        minWidth: DEFAULT_MIN_WIDTH,
        minHeight: DEFAULT_MIN_HEIGHT,
        preserveAspectRatio: true,
      },
    };
  },

  addAttributes() {
    return {
      src: { default: "" },
      title: { default: "iFrame" },
      allow: { default: null },
      frameborder: { default: "0" },
      allowfullscreen: { default: "true" },
      width: { default: null },
      height: { default: null },
    };
  },

  parseHTML() {
    return [
      {
        tag: "iframe",
        getAttrs: (element) => {
          const el = element as HTMLIFrameElement;
          return {
            src: el.getAttribute("src"),
            title: el.getAttribute("title") || "iFrame",
            allow: el.getAttribute("allow"),
            frameborder: el.getAttribute("frameborder") || "0",
            allowfullscreen: el.getAttribute("allowfullscreen") || "true",
            width: el.getAttribute("width"),
            height: el.getAttribute("height"),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["iframe", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ({ node, getPos, editor, HTMLAttributes }) => {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("title", String(node.attrs.title || "iFrame"));
      iframe.setAttribute("frameborder", String(node.attrs.frameborder || "0"));
      if (node.attrs.allow) {
        iframe.setAttribute("allow", String(node.attrs.allow));
      }
      if (node.attrs.allowfullscreen) {
        iframe.setAttribute("allowfullscreen", "true");
      }
      if (node.attrs.src) {
        iframe.src = String(node.attrs.src);
      }

      // Copy through any extra HTML attributes TipTap rendered for the node.
      Object.entries(HTMLAttributes).forEach(([key, value]) => {
        if (
          value == null ||
          key === "src" ||
          key === "width" ||
          key === "height"
        ) {
          return;
        }
        iframe.setAttribute(key, String(value));
      });

      iframe.style.display = "block";
      iframe.style.maxWidth = "100%";
      iframe.style.border = "0";
      iframe.style.width = `${node.attrs.width || DEFAULT_WIDTH}px`;
      iframe.style.height = `${node.attrs.height || DEFAULT_HEIGHT}px`;

      const resize = this.options.resize;
      if (!resize?.enabled || typeof document === "undefined") {
        return { dom: iframe };
      }

      return createResizableNodeView({
        element: iframe,
        node,
        editor,
        getPos,
        nodeName: this.name,
        minWidth: resize.minWidth ?? DEFAULT_MIN_WIDTH,
        minHeight: resize.minHeight ?? DEFAULT_MIN_HEIGHT,
        preserveAspectRatio: resize.preserveAspectRatio ?? true,
        onUpdate: (updatedNode) => {
          if (updatedNode.attrs.src) {
            iframe.src = String(updatedNode.attrs.src);
          }
          if (updatedNode.attrs.title) {
            iframe.title = String(updatedNode.attrs.title);
          }
          return true;
        },
      });
    };
  },

  addCommands() {
    return {
      addIframe:
        (attrs) =>
        ({ commands }) => {
          if (!attrs.src) {
            return false;
          }
          return commands.insertContent({ type: this.name, attrs });
        },
    };
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: iframeRegex,
        type: this.type,
        getAttributes: (match) => {
          const iframe = match[0];
          return {
            src: extractHTMLTag(iframe, "src"),
            title: extractHTMLTag(iframe, "title") ?? "iFrame",
            allow: extractHTMLTag(iframe, "allow"),
            width: extractHTMLTag(iframe, "width"),
            height: extractHTMLTag(iframe, "height"),
          };
        },
      }),
    ];
  },
});
